import { useRef, useEffect } from 'react';
import { Input } from 'reactstrap';
import { setRef } from '../../utils/refs';

const getScrollableParent = (el) => {
  let node = el.parentNode;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    const isScrollable =
      !['visible', 'hidden'].includes(overflowY) &&
      node.scrollHeight > node.clientHeight;
    if (isScrollable) return node;
    node = node.parentNode;
  }
  // Fallback to window if no scrollable parent is found
  return window;
};

const onWheel = (e) => {
  if (e.target !== document.activeElement) return;
  // This prevents the value from changing, but also the
  // scrolling of the page/parent element
  e.preventDefault();
  // Find and scroll the parent element, which was prevented
  const scrollableParent = getScrollableParent(e.target);
  if (scrollableParent === window) {
    window.scrollBy(0, e.deltaY);
  } else {
    scrollableParent.scrollTop += e.deltaY;
  }
};

// FormInput is a wrapper around an input/Input that prevents
// the default value scrolling behavior for number inputs.
const FormInput = ({ innerRef, tag: Tag = Input, ...props }) => {
  const { type } = props;
  const localInputRef = useRef();

  // Prevent scrolling from changing number input values.
  useEffect(() => {
    if (type !== 'number') return null;

    localInputRef.current.addEventListener('wheel', onWheel, {
      // Browsers will ignore the `preventDefault` if this is not set
      passive: false,
    });
    return () => {
      if (!localInputRef.current) return;
      localInputRef.current.removeEventListener('wheel', onWheel);
    };
  }, [type]);

  const setRefs = (el) => {
    // Set the local ref and the parent ref
    localInputRef.current = el;
    setRef(innerRef, el);
  };

  return Tag === 'input' ? (
    <input ref={setRefs} {...props} />
  ) : Tag === 'textarea' ? (
    <textarea ref={setRefs} {...props} />
  ) : Tag === 'select' ? (
    <select ref={setRefs} {...props} />
  ) : (
    <Tag innerRef={setRefs} {...props} />
  );
};

export default FormInput;
